import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Eye, Edit, Trash2, Clock, MapPin, FileText, User, Filter, Star } from 'lucide-react';
import { useApp } from '../App';
import { tasksService, usersService } from '../services/api';

const TaskScheduler = () => {
  const { db, setDb, currentUser } = useApp();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [filterUser, setFilterUser] = useState('all');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');

  // Estado para el formulario de crear tarea (sin nombre y bodega)
  const [formData, setFormData] = useState({
    descripcion: '',
    assignedTo: ''
  });

  // Cargar lista de usuarios al montar el componente
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        setUsersError(null);
        const usersList = await usersService.getUsers();
        setUsers(usersList);
        setLoadingUsers(false);
      } catch (error) {
        console.error('❌ Error cargando usuarios:', error);
        setUsersError(error.message);
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);
  
  // Recargar usuarios cuando se abre el modal
  useEffect(() => {
    if (showCreateModal && users.length === 0) {
      const loadUsers = async () => {
        try {
          const usersList = await usersService.getUsers();
          setUsers(usersList);
          setLoadingUsers(false);
        } catch (error) {
          setUsersError(error.message);
          setLoadingUsers(false);
        }
      };
      loadUsers();
    }
  }, [showCreateModal]);

  // Cargar tareas automáticamente
  useEffect(() => {
    const loadTasks = async () => {
      try {
        const tasks = await tasksService.getTasks();
        setDb(prevDb => ({
          ...prevDb,
          scheduledTasks: tasks,
          tasks: tasks
        }));
      } catch (error) {
        console.error('Error cargando tareas:', error);
      }
    };
    loadTasks();
  }, [setDb]);

  const scheduledTasks = db.scheduledTasks || [];
  
  // Filtrar tareas por usuario y fecha
  const filteredTasks = scheduledTasks.filter(task => {
    // Filtro por usuario
    if (filterUser !== 'all' && task.assignedTo !== filterUser) {
      return false;
    }
    
    // Filtro por fecha desde
    if (filterFechaDesde) {
      const taskDate = new Date(task.fechaCreacion).toISOString().split('T')[0];
      if (taskDate < filterFechaDesde) return false;
    }
    
    // Filtro por fecha hasta
    if (filterFechaHasta) {
      const taskDate = new Date(task.fechaCreacion).toISOString().split('T')[0];
      if (taskDate > filterFechaHasta) return false;
    }
    
    return true;
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Guardar calificación de tarea
  const handleRateTask = async (taskId, rating) => {
    try {
      await tasksService.updateTaskRating(taskId, rating);
      // Actualizar localmente
      setDb(prevDb => ({
        ...prevDb,
        scheduledTasks: prevDb.scheduledTasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, calificacion: rating, ratedBy: currentUser?.user?.username || currentUser?.username || 'Sistema' } : t
        ),
        tasks: prevDb.tasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, calificacion: rating, ratedBy: currentUser?.user?.username || currentUser?.username || 'Sistema' } : t
        )
      }));
    } catch (error) {
      console.error('Error calificando tarea:', error);
    }
  };

  // Componente de estrellas (solo lectura si ya está calificado)
  const StarRating = ({ taskId, currentRating = 0, ratedBy = null, onRate, readOnly = false }) => {
    const [hoverRating, setHoverRating] = useState(0);
    const isRated = currentRating > 0;
    const isDisabled = readOnly || isRated;
    
    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  onRate(taskId, star);
                }
              }}
              onMouseEnter={() => !isDisabled && setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className={`transition-colors ${isDisabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
              disabled={isDisabled}
            >
              <Star
                size={18}
                className={`${
                  star <= (hoverRating || currentRating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                } transition-colors`}
              />
            </button>
          ))}
        </div>
        {isRated && ratedBy && (
          <span className="text-xs text-purple-600">
            Calificado por: {ratedBy}
          </span>
        )}
      </div>
    );
  };

  const saveTask = async () => {
    if (!formData.assignedTo) {
      alert('Por favor seleccione un usuario para asignar la tarea');
      return;
    }
    if (!formData.descripcion.trim()) {
      alert('Por favor ingrese la descripción de la tarea');
      return;
    }

    try {
      const taskData = {
        nombre: 'Tarea', // Nombre genérico
        bodega: 'General', // Bodega genérica
        descripcion: formData.descripcion,
        estado: 'programada',
        assignedTo: formData.assignedTo,
        assignedBy: currentUser?.user?.username || currentUser?.username || 'Sistema',
        calificacion: 0
      };

      const newTask = await tasksService.createTask(taskData);
      const updatedTasks = await tasksService.getTasks();
      setDb(prevDb => ({
        ...prevDb,
        scheduledTasks: updatedTasks,
        tasks: updatedTasks
      }));

      window.dispatchEvent(new CustomEvent('taskCreated', { 
        detail: { newTask, updatedTasks } 
      }));

      setFormData({ descripcion: '', assignedTo: '' });
      setShowCreateModal(false);

    } catch (error) {
      console.error('❌ Error creando tarea:', error);
      alert('Error al crear la tarea: ' + error.message);
    }
  };

  const viewTaskDetails = (task) => {
    setSelectedTask(task);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'programada': return 'bg-blue-100 text-blue-800';
      case 'en-progreso': return 'bg-yellow-100 text-yellow-800';
      case 'completada': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'programada': return 'Programada';
      case 'en-progreso': return 'En Progreso';
      case 'completada': return 'Completada';
      case 'cancelada': return 'Cancelada';
      default: return status;
    }
  };

  const limpiarFiltros = () => {
    setFilterUser('all');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Calendar className="text-indigo-500" size={32} />
            Programador de Tareas
          </h2>
          <p className="text-gray-600 mt-2">
            Gestión y programación de tareas
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Crear Tarea
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-indigo-50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={24} className="text-indigo-600" />
            <h3 className="font-semibold text-indigo-800">Total Tareas</h3>
          </div>
          <p className="text-3xl font-bold text-indigo-600">{scheduledTasks.length}</p>
        </div>

        <div className="bg-blue-50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Clock size={24} className="text-blue-600" />
            <h3 className="font-semibold text-blue-800">Programadas</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {scheduledTasks.filter(t => t.estado === 'programada').length}
          </p>
        </div>

        <div className="bg-yellow-50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Clock size={24} className="text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">En Progreso</h3>
          </div>
          <p className="text-3xl font-bold text-yellow-600">
            {scheduledTasks.filter(t => t.estado === 'en-progreso').length}
          </p>
        </div>

        <div className="bg-green-50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={24} className="text-green-600" />
            <h3 className="font-semibold text-green-800">Completadas</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {scheduledTasks.filter(t => t.estado === 'completada').length}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Filtro por Usuario */}
          <div className="flex items-center gap-2">
            <User size={18} className="text-gray-500" />
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">Todos los usuarios</option>
              {users.map(user => (
                <option key={user._id} value={user.username}>
                  {user.name} ({user.username})
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por Fecha Desde */}
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-500" />
            <input
              type="date"
              value={filterFechaDesde}
              onChange={(e) => setFilterFechaDesde(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Desde"
            />
          </div>

          {/* Filtro por Fecha Hasta */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">hasta</span>
            <input
              type="date"
              value={filterFechaHasta}
              onChange={(e) => setFilterFechaHasta(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Hasta"
            />
          </div>

          {/* Botón limpiar filtros */}
          <button
            onClick={limpiarFiltros}
            className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            Limpiar filtros
          </button>

          <div className="ml-auto text-sm text-gray-500">
            Mostrando {filteredTasks.length} de {scheduledTasks.length} tarea(s)
          </div>
        </div>
      </div>

      {/* Lista de Tareas */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-semibold mb-6">Tareas Programadas</h3>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-4 text-gray-600 font-medium">Calificación</th>
                <th className="text-left p-4 text-gray-600 font-medium">Asignado a</th>
                <th className="text-left p-4 text-gray-600 font-medium">Descripción</th>
                <th className="text-left p-4 text-gray-600 font-medium">Estado</th>
                <th className="text-left p-4 text-gray-600 font-medium">Fecha</th>
                <th className="text-center p-4 text-gray-600 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-12 text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <Calendar size={48} className="text-gray-300" />
                      <span className="text-lg">No hay tareas programadas</span>
                      <span className="text-sm">
                        Use el botón "Crear Tarea" para programar nuevas tareas
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTasks.map(task => (
                  <tr key={task.id || task._id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <StarRating
                        taskId={task.id || task._id}
                        currentRating={task.calificacion || 0}
                        ratedBy={task.ratedBy}
                        onRate={handleRateTask}
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="text-gray-700 font-medium">
                          {task.assignedTo || 'No asignado'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600 max-w-md">
                      <p className="line-clamp-2" title={task.descripcion}>
                        {task.descripcion}
                      </p>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.estado)}`}>
                        {getStatusText(task.estado)}
                      </span>
                    </td>
                    <td className="p-4 text-gray-600 text-sm">
                      {new Date(task.fechaCreacion).toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => viewTaskDetails(task)}
                          className="text-blue-500 hover:text-blue-700 transition-colors p-2 rounded hover:bg-blue-50"
                          title="Ver detalles"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Crear Tarea */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-indigo-600">Crear Nueva Tarea</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {loadingUsers && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">⏳ Cargando usuarios...</p>
              </div>
            )}
            
            {usersError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">❌ Error: {usersError}</p>
              </div>
            )}
            
            <div className="space-y-6">
              {/* Asignar a Usuario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asignar a Usuario *
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  disabled={loadingUsers}
                >
                  <option value="">
                    {loadingUsers ? 'Cargando usuarios...' : 'Seleccione un usuario'}
                  </option>
                  {users.map(user => (
                    <option key={user._id || user.id} value={user.username}>
                      {user.name} ({user.username})
                    </option>
                  ))}
                </select>
              </div>

              {/* Descripción de la Tarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción de la Tarea *
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                  placeholder="Describe detalladamente la tarea a realizar..."
                />
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={saveTask}
                  className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Programar Tarea
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles */}
      {showDetailsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-indigo-600">Detalles de la Tarea</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium">Calificación:</span>
                <StarRating
                  taskId={selectedTask.id || selectedTask._id}
                  currentRating={selectedTask.calificacion || 0}
                  ratedBy={selectedTask.ratedBy}
                  onRate={(id, rating) => {
                    handleRateTask(id, rating);
                    setSelectedTask(prev => ({ ...prev, calificacion: rating, ratedBy: currentUser?.user?.username || currentUser?.username || 'Sistema' }));
                  }}
                />
              </div>
              
              <div>
                <span className="text-gray-600 font-medium">Asignado a:</span>
                <p className="text-gray-800">{selectedTask.assignedTo || 'No asignado'}</p>
              </div>
              
              <div>
                <span className="text-gray-600 font-medium">Estado:</span>
                <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTask.estado)}`}>
                  {getStatusText(selectedTask.estado)}
                </span>
              </div>
              
              <div>
                <span className="text-gray-600 font-medium">Descripción:</span>
                <p className="text-gray-800 mt-1 bg-gray-50 p-3 rounded-lg">{selectedTask.descripcion}</p>
              </div>
              
              <div>
                <span className="text-gray-600 font-medium">Fecha de creación:</span>
                <p className="text-gray-800">
                  {new Date(selectedTask.fechaCreacion).toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              
              {selectedTask.createdBy && (
                <div>
                  <span className="text-gray-600 font-medium">Programado por:</span>
                  <p className="text-purple-600 font-medium">{selectedTask.createdBy}</p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowDetailsModal(false)}
              className="w-full mt-6 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskScheduler;
